import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { PageLayout, Button } from "../common/index.js"
import DateFilterDropdown from "./DateFilterDropdown.js"
import analyticsService from "../../services/AnalyticsService"
import { TrendingUp, TrendingDown, DollarSign, ArrowLeft, PieChart } from "lucide-react"

const ProfitLossAnalytics = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [filter, setFilter] = useState("current_month")
  const [customDates, setCustomDates] = useState({ from_date: "", to_date: "" })

  useEffect(() => {
    fetchData()
  }, [filter])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = filter === "custom" ? { filter, ...customDates } : { filter }
      const response = await analyticsService.getProfitLoss(params)
      setData(response)
    } catch (error) {
      console.error("Error fetching profit/loss analytics:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0)
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color, large }) => (
    <div className={`bg-white rounded-card shadow-card p-4 lg:p-6 ${large ? "lg:col-span-2" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className={`${large ? "text-2xl lg:text-4xl" : "text-xl lg:text-2xl"} font-bold text-gray-800`}>
            {value}
          </h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className={`${large ? "w-8 h-8" : "w-5 h-5 lg:w-6 lg:h-6"} text-white`} />
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <PageLayout title="Profit & Loss Report" icon={TrendingUp}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading P&L analytics...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Profit & Loss Report"
      icon={TrendingUp}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate("/analytics")}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Comprehensive financial overview</p>

                {/* Main Summary */}
                {data && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Total Revenue"
                        value={formatCurrency(data.summary.total_revenue)}
                        subtitle="Income generated"
                        icon={DollarSign}
                        color="bg-green-500"
                      />
                      <StatCard
                        title="Total Expenses"
                        value={formatCurrency(data.summary.total_expenses)}
                        subtitle="Total expenditure"
                        icon={DollarSign}
                        color="bg-red-500"
                      />
                      <StatCard
                        title="Net Profit/Loss"
                        value={formatCurrency(data.summary.net_profit_loss)}
                        subtitle={data.summary.status === "profit" ? "Profit" : "Loss"}
                        icon={data.summary.status === "profit" ? TrendingUp : TrendingDown}
                        color={data.summary.status === "profit" ? "bg-emerald-600" : "bg-red-600"}
                        large
                      />
                      <StatCard
                        title="Profit Margin"
                        value={`${data.summary.profit_margin_percentage.toFixed(2)}%`}
                        subtitle="Revenue to profit ratio"
                        icon={PieChart}
                        color="bg-purple-500"
                      />
                    </div>

                    {/* Revenue Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <TrendingUp className="w-6 h-6 text-green-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Revenue Breakdown</h2>
                        </div>

                        <div className="space-y-4">
                          {/* Shipments */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Shipments</h3>
                            <div className="bg-green-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Revenue:</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.total_revenue)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Collected:</span>
                                <span className="text-sm font-semibold text-emerald-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.collected)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending:</span>
                                <span className="text-sm font-semibold text-blue-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.pending)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-green-100">
                                <span>Shipment Count:</span>
                                <span>{data.revenue_breakdown.shipments.shipment_count}</span>
                              </div>
                            </div>
                          </div>

                          {/* Loans Recovered */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Loans Recovered</h3>
                            <div className="bg-brand-light p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Given:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(data.revenue_breakdown.loans_recovered.total_given)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Received Back:</span>
                                <span className="text-sm font-semibold text-brand">
                                  {formatCurrency(data.revenue_breakdown.loans_recovered.received_back)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending Recovery:</span>
                                <span className="text-sm font-semibold text-blue-600">
                                  {formatCurrency(data.revenue_breakdown.loans_recovered.pending_recovery)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Payment Mode</h3>
                            <div className="space-y-2">
                              {Object.entries(data.revenue_breakdown.by_payment_mode).length > 0 ? (
                                Object.entries(data.revenue_breakdown.by_payment_mode).map(([mode, stats]) => (
                                  <div key={mode} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700 capitalize">{mode.replace(/_/g, " ")}</span>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-gray-800">{formatCurrency(stats.amount)}</p>
                                      <p className="text-xs text-gray-500">{stats.count} shipments</p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-2 bg-gray-50 rounded text-sm text-gray-500">No data available</div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.revenue_breakdown.by_status).length > 0 ? (
                                Object.entries(data.revenue_breakdown.by_status).map(([status, stats]) => (
                                  <div key={status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700 capitalize">{status.replace(/_/g, " ")}</span>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-gray-800">{formatCurrency(stats.amount)}</p>
                                      <p className="text-xs text-gray-500">{stats.count} shipments</p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-2 bg-gray-50 rounded text-sm text-gray-500">No data available</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expense Breakdown */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <TrendingDown className="w-6 h-6 text-red-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Expense Breakdown</h2>
                        </div>

                        <div className="space-y-4">
                          {/* Salaries */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Salaries</h3>
                            <div className="bg-red-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Salary:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(data.expense_breakdown.salaries.total_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Paid:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.expense_breakdown.salaries.paid_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending:</span>
                                <span className="text-sm font-semibold text-blue-600">
                                  {formatCurrency(data.expense_breakdown.salaries.pending_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-red-100">
                                <span>Employees:</span>
                                <span>{data.expense_breakdown.salaries.employee_count}</span>
                              </div>
                            </div>
                          </div>

                          {/* Office Expenses - NEW */}
                          {data.expense_breakdown.office_expenses && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 mb-2">Office Expenses</h3>
                              <div className="bg-purple-50 p-3 rounded space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Total Expenses:</span>
                                  <span className="text-sm font-semibold text-gray-800">
                                    {formatCurrency(data.expense_breakdown.office_expenses.total_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Paid:</span>
                                  <span className="text-sm font-semibold text-purple-600">
                                    {formatCurrency(data.expense_breakdown.office_expenses.paid_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Pending:</span>
                                  <span className="text-sm font-semibold text-blue-600">
                                    {formatCurrency(data.expense_breakdown.office_expenses.pending_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-purple-100">
                                  <span>Expense Count:</span>
                                  <span>{data.expense_breakdown.office_expenses.expense_count}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Loan Repayments */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Loan Repayments</h3>
                            <div className="bg-blue-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Received:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(data.expense_breakdown.loan_repayments.total_received)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Paid Back:</span>
                                <span className="text-sm font-semibold text-blue-600">
                                  {formatCurrency(data.expense_breakdown.loan_repayments.paid_back)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending Payment:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.expense_breakdown.loan_repayments.pending_payment)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Refunds */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Refunds</h3>
                            <div className="bg-yellow-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Refunded:</span>
                                <span className="text-sm font-semibold text-yellow-600">
                                  {formatCurrency(data.expense_breakdown.refunds.total_refunded)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Refund Count:</span>
                                <span>{data.expense_breakdown.refunds.refund_count}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Outstanding */}
                    <div className="bg-white rounded-card shadow-card p-4 lg:p-6 mb-6">
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Outstanding Amounts</h2>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="p-4 bg-brand-light rounded-sm">
                          <p className="text-sm text-gray-600 mb-1">Receivables</p>
                          <p className="text-2xl font-bold text-brand">{formatCurrency(data.outstanding.receivables)}</p>
                          <p className="text-xs text-gray-500 mt-1">Amount to be received</p>
                        </div>
                        <div className="p-4 bg-red-50 rounded-sm">
                          <p className="text-sm text-gray-600 mb-1">Payables</p>
                          <p className="text-2xl font-bold text-red-600">{formatCurrency(data.outstanding.payables)}</p>
                          <p className="text-xs text-gray-500 mt-1">Amount to be paid</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-sm">
                          <p className="text-sm text-gray-600 mb-1">Net Outstanding</p>
                          <p
                            className={`text-2xl font-bold ${data.outstanding.net_outstanding >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {formatCurrency(data.outstanding.net_outstanding)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {data.outstanding.net_outstanding >= 0 ? "In favor" : "In deficit"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Trend */}
                    {data.monthly_trend && Object.keys(data.monthly_trend).length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Trend</h2>
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                  Revenue
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                  Salary
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                  Office
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                  Total Expense
                                </th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                  Profit/Loss
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {Object.entries(data.monthly_trend).map(([month, trend]) => (
                                <tr key={month} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">{month}</td>
                                  <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                                    {formatCurrency(trend.revenue)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-600">
                                    {formatCurrency(trend.salary_expense)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-600">
                                    {formatCurrency(trend.office_expense)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                                    {formatCurrency(trend.total_expense)}
                                  </td>
                                  <td
                                    className={`px-4 py-3 text-sm text-right font-bold ${
                                      trend.profit >= 0 ? "text-emerald-600" : "text-red-600"
                                    }`}
                                  >
                                    {formatCurrency(trend.profit)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
    </PageLayout>
  )
}

export default ProfitLossAnalytics;